import { useTranslation } from 'next-i18next';
import Router from 'next/router';
import React, { useState, useEffect } from 'react';

import { NS } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/redux';
import { loadSensefuel } from '@/redux/epics/configuration.epic';
import { closeSearch, openSearch } from '@/redux/slices/gui.slice';
import { useServices } from '@/services/useServices';
import { formatUrl } from '@/utils/linkUtils';
import { isKrugOrWhispering } from '@/utils/millesimaUtils';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './Search.module.scss';
import { PAGES } from '@/constants/pages';
import { SEO_TYPES } from '@/constants/seo';
import { URLS } from '@/constants/urls';

const MINIMUM_LENGTH_TO_SUGGEST = 2;

type SearchProps = {
  isMobile?: boolean;
}

export const Search = ({ isMobile = false }: SearchProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(NS.COMMON);
  const { storeConf } = useAppSelector(({ root }) => root);
  const isEc = useAppSelector(({ auth }) => !!auth.loggedUser?.isEc);
  const { searchOpen } = useAppSelector(({ gui }) => gui);

  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestionOpened, setSuggestionOpened] = useState(false);
  const [suggestions, setSuggestions] = useState<any>({});

  const { productsService, datalayerService } = useServices();

  const buildRoute = ({ seoKeyword, type, id }) => {
    const asPath: any = {
      pathname: seoKeyword ?
        formatUrl(seoKeyword, [SEO_TYPES.CATEG_SPIRIT, SEO_TYPES.PRODUCT_SPIRIT].includes(type), storeConf) :
        searchTerm ? PAGES.SEARCH : 'tous-nos-vins'
    };

    const url: any = {
      pathname: [SEO_TYPES.CATEG_SPIRIT, SEO_TYPES.PRODUCT_SPIRIT].includes(type) ? '/spirits/[...spiritSlug]' : '/[...slug]',
      query: {
        slug: [seoKeyword ? seoKeyword : searchTerm ? PAGES.SEARCH : 'tous-nos-vins']
      }
    };

    if (seoKeyword) {
      if (type) {
        url.query.type = type;
      }

      if (id) {
        url.query.id = id;
      }
    } else if (searchTerm) {
      url.query.searchTerm = searchTerm;
      asPath.query = { searchTerm: url.query.searchTerm };
    }

    if (seoKeyword?.includes('?')) {
      const params = new URLSearchParams(
        seoKeyword.slice(seoKeyword.indexOf('?'))
      );
      const newPathname = formatUrl(
        seoKeyword.substr(0, seoKeyword.indexOf('?')),
        [SEO_TYPES.CATEG_SPIRIT, SEO_TYPES.PRODUCT_SPIRIT].includes(type),
        storeConf
      );

      asPath.pathname = newPathname;
      asPath.query = {
        ...asPath.query,
        ...Object.fromEntries(params)
      };

      url.pathname = newPathname;
      url.query = {
        ...asPath.query,
        ...Object.fromEntries(params)
      };
    }

    return { asPath, url };
  };

  const resetSearch = () => {
    setIsFocused(false);
    setSuggestions([]);
    setSearchTerm('');
    setSuggestionOpened(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    dispatch(loadSensefuel());
  };

  const handleBlur = () => {
    if (!isMobile) {
      setTimeout(() => {
        resetSearch();
      }, 500);
    }
  };

  const handleSearchTermChange = (newValue) => {
    setSearchTerm(newValue);
  };

  const handleOnClose = () => {
    resetSearch();
    if (isMobile) {
      dispatch(closeSearch());
    }
  };

  const handleSeeMore = ({ seoKeyword, type, id }: any) => {
    const routerConfig = buildRoute({ seoKeyword, type, id });

    datalayerService.pushSearchSuggest(
      routerConfig.url.query?.type,
      routerConfig.url.query?.id,
      searchTerm,
      routerConfig.url.query?.slug?.[0]
    );

    if (isKrugOrWhispering(routerConfig.asPath.pathname) || isKrugOrWhispering(routerConfig.url.query.slug?.[0])) {
      window.location.href = routerConfig.asPath.pathname || routerConfig.url.query.slug[0];
    } else {
      Router.push(routerConfig.url, routerConfig.asPath, { locale: Router.locale });
    }

    resetSearch();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSeeMore({});
    }
  };

  useEffect(() => {
    if (isMobile && !searchOpen) {
      resetSearch();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (searchTerm.length >= MINIMUM_LENGTH_TO_SUGGEST) {
      productsService.getSuggest(searchTerm, storeConf.storeId, !!isEc).then((res) => {
        const suggestionsProduct = [];
        const suggestionsCategory = [];

        res.forEach((suggestion) => {
          if ([SEO_TYPES.PRODUCT, SEO_TYPES.PRODUCT_SPIRIT].includes(suggestion.type)) {
            suggestionsProduct.push(suggestion);
          } else if ([SEO_TYPES.CATEG, SEO_TYPES.CATEG_SPIRIT, SEO_TYPES.HTMLCONTENT].includes(suggestion.type)) {
            suggestionsCategory.push(suggestion);
          }
        });

        setSuggestions({ product: suggestionsProduct, category: suggestionsCategory });
        setSuggestionOpened(true);
      });
    }

    if (searchTerm.length > 1) {
      setSuggestionOpened(true);
      if (isMobile && !searchOpen) {
        dispatch(openSearch());
      }
    }
    if (searchTerm.length === 0) {
      setSuggestionOpened(false);
      if (isMobile && searchOpen) {
        dispatch(closeSearch());
      }
    }
  }, [searchTerm]);

  return (
    <div className={`${styles.search} ${(isMobile) ? styles['search-is-mobile'] : ''} ${searchOpen ? styles['mobile-open'] : ''}`} data-rbf="navbar-search" data-test-id="navbar-search">
      <div className={`${styles['search-bar']} ${isFocused ? styles.focused : ''} search-bar`}>
        <img
          onClick={() => handleSeeMore({})}
          src={URLS.ICON_SEARCH}
          className={styles.icon}
          alt="search"
          data-test-id="icon-search"
          data-rbf="icon-search"
          width={24} height={24}
        />
        <input
          sensefuel-id="input-search"
          data-test-id="input-search"
          data-rbf="input-search"
          value={searchTerm}
          placeholder={t('btn.search')}
          onChange={(e) => handleSearchTermChange(e.target.value)}
          onFocus={() => handleFocus()}
          onBlur={() => handleBlur()}
          onKeyDown={handleKeyDown}
        />
        {searchTerm.length > 0 &&
          <img
            onClick={() => handleOnClose()}
            src={URLS.ICON_CLOSE}
            className={styles.icon}
            alt="close"
            data-test-id="close-search"
            data-rbf="close-search"
            width={16} height={16}
          />
        }
      </div>
      {suggestionOpened &&
        <div className={styles.suggestions}>
          <div className={styles['suggestions-product']}>
            <div className={styles['list']}>
              <strong className={styles.title}>{t('searchBar.article')}</strong>
              {suggestions.product?.length > 0 && suggestions.product.map((suggestion) => (
                <a key={suggestion.id} className={`${styles.item} ${styles['item-article']}`} data-test-id={`suggestion-${suggestion.type}`} onClick={() => handleSeeMore(suggestion)}>{suggestion.name}</a>
              ))}
            </div>
            <div className={styles['see-more']}>
              <CallToActionContainer
                id="see-more-search"
                onClick={() => handleSeeMore({})}
                fullWidth
              >
                {t('searchBar.more')}
              </CallToActionContainer>
            </div>
          </div>
          <div className={styles['suggestions-category']}>
            <div className={styles['list']}>
              <strong className={styles.title}>{t('searchBar.category')}</strong>
              {suggestions.category?.length > 0 && suggestions.category.map((suggestion) => (
                <a key={suggestion.id} className={styles.item} data-test-id={`suggestion-${suggestion.type}`} onClick={() => handleSeeMore(suggestion)}>{suggestion.name}</a>
              ))}
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default Search;
